import { type ExternalFile, ExternalFileSource } from '@/lib/types';
import googleDriveApiClient from './googleDriveApiClient';
import GoogleDriveLargeFileUploader from './GoogleDriveLargeFileUploader';
import { LARGE_FILE_THRESHOLD } from '../../utils/upload/LargeFileUploader/AbstractLargeFileUploader';
import getGoogleDriveFilePublicUrl from './getGoogleDriveFilePublicUrl';

export type GoogleDriveUploadResponse = {
  id: string;
  kind: string;
  mimeType: string;
  name: string;
};

const performSimpleUpload = (
  file: File,
): Promise<GoogleDriveUploadResponse> => {
  const uploadMetadata = {
    name: file.name,
    mimeType: file.type,
  };

  const uploadRequestFormData = new FormData();

  uploadRequestFormData.append(
    'metadata',
    new Blob([JSON.stringify(uploadMetadata)], {
      type: 'application/json',
    }),
  );

  uploadRequestFormData.append('file', file);

  return googleDriveApiClient.post<GoogleDriveUploadResponse>(
    '/upload/drive/v3/files?uploadType=multipart',
    uploadRequestFormData,
  );
};

const performUpload = async (
  file: File,
  onProgressUpdate: (progress: number) => void,
): Promise<GoogleDriveUploadResponse> => {
  if (file.size > LARGE_FILE_THRESHOLD) {
    return new GoogleDriveLargeFileUploader().uploadLargeFile(
      file,
      onProgressUpdate,
    );
  }

  return performSimpleUpload(file);
};

const uploadFileToGoogleDrive = async (
  file: File,
  onProgressUpdate: (progress: number) => void,
): Promise<ExternalFile> => {
  const googleDriveFile = await performUpload(file, onProgressUpdate);

  const googleDriveFilePublicUrl = await getGoogleDriveFilePublicUrl(
    googleDriveFile.id,
  );

  return {
    source: ExternalFileSource.GOOGLE_DRIVE,
    externalId: googleDriveFile.id,
    filename: googleDriveFile.name,
    url: googleDriveFilePublicUrl,
    mimeType: googleDriveFile.mimeType,
  };
};

export default uploadFileToGoogleDrive;
